import './styles/supportDialog.css';
import Dialog from '../../subcomponents/Dialog';
import Btn from '../../subcomponents/Btn';
import { useEffect, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { useMagicLinkUnlock } from '../../hooks/useMagicLinkUnlock';
import CustomToast from '../../subcomponents/Toast';
import toast from 'react-hot-toast';
import TextField from '../../subcomponents/TextField';
import { isKoFiUnlocked } from '../../utils/unlockablesHelper';
import { Heart } from 'lucide-react';

type SupportDialogProps = {
  open: boolean;
  onClose: () => void;
};

function SupportDialog(props: SupportDialogProps) {
  const { open, onClose } = props;

  const [email, setEmail] = useState('');
  const [emailSent, setEmailSent] = useState<null | 'success' | 'fail' | 'loading'>(null);
  const isWeb = Capacitor.getPlatform() === 'web';

  const magicLinkStatus = useMagicLinkUnlock();

  useEffect(() => {
    if (magicLinkStatus === 'success') {
      toast.custom((t) => <CustomToast t={t} message="Email verified" success />);
    }

    if (magicLinkStatus === 'fail') {
      toast.custom((t) => <CustomToast t={t} message="Invalid or expired link" warning />);
    }
  }, [magicLinkStatus]);

  const sendMagicLink = async () => {
    if (!email) {
      toast.custom((t) => <CustomToast t={t} message="Enter an email" warning />);
      return;
    }

    setEmailSent('loading');

    const res = await fetch('/.netlify/functions/sendMagicLink', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    });

    const { success } = await res.json();

    setEmailSent(success ? 'success' : 'fail');

    if (success) {
      toast.custom((t) => <CustomToast t={t} message="Confirmation email sent" success />);
    } else {
      toast.custom((t) => <CustomToast t={t} message="Email address not found" warning />);
    }
  };

  const getTitle = () => {
    if (isKoFiUnlocked() && isWeb) return 'THANK YOU!';
    return 'SUPPORT';
  };

  return (
    <Dialog open={open} onClose={onClose} title={getTitle()}>
      {isWeb && (
        <div>
          {!isKoFiUnlocked() && (
            <>
              <div className="support-text">
                If you'd like to support my future development and help keep the game ad-free,
                consider visiting my Ko-fi page!
              </div>
              <div className="flex justify-center mt-4">
                <Btn
                  anchorLink
                  link="https://ko-fi.com/letterswap"
                  appendIcon="Heart"
                  text="SUPPORT VIA KO-FI"
                  redIcon
                  onClick={() => {}}
                />
              </div>
            </>
          )}
          {isKoFiUnlocked() && (
            <>
              <div className="support-text">
                Your donation unlocked data transfer feature and new animation colors. Head to
                <b> STATISTICS </b> to check them out!
              </div>
              <div className="flex justify-center mt-4">
                <Heart className="big-heart-icon" />
              </div>
            </>
          )}
          <div>
            {!isKoFiUnlocked() && <hr className="support-divider" />}
            <div className="flex flex-col items-center gap-3">
              {!isKoFiUnlocked() && (
                <>
                  <div className="mb-2 support-subtitle">ALREADY SUPPORTED?</div>
                  <div className="support-sibtitle-desc">
                    Tip any amount on Ko-fi to unlock data transfer feature and all additional found
                    animation colors.
                  </div>
                  <div className="email-confirmation-text -mb-1">Email you supported with:</div>
                  <TextField
                    value={email}
                    email
                    placeholder="[ your@email.com ]"
                    onChange={setEmail}
                    maxLength={200}
                    fullWidth
                    truncate
                  />
                  {emailSent !== 'success' && (
                    <div>
                      <Btn
                        text="SEND VERIFICATION LINK"
                        onClick={sendMagicLink}
                        loading={emailSent === 'loading'}
                      />
                    </div>
                  )}
                  {emailSent === 'success' && (
                    <div className="email-confirmation-text">Check inbox to verify</div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
      {!isWeb && (
        <div>
          <div className="support-text">
            If you'd like to support my future development and help keep the game ad-free, check out
            my merch store!
          </div>
          <div className="flex justify-center mt-4">
            <Btn
              anchorLink
              link="https://letterswap.printful.me/"
              appendIcon="Heart"
              text="VISIT STORE"
              redIcon
              onClick={() => {}}
            />
          </div>
        </div>
      )}
    </Dialog>
  );
}

export default SupportDialog;
